import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutGrid,
  ListChecks,
  Settings,
  LogOut,
  Menu,
  ChevronLeft,
  ChevronRight,
  Link2,
  Sparkles,
  Store,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Logo } from './Logo';

// Navegação principal intencionalmente curta e orientada ao trabalho
// (Missão WhatsApp Omnichannel): Visão geral, Atividades, Criar anúncio,
// Anúncios, Integrações, Configurações. Magic Place (/product-analyzer) e
// Radar de Mercado (/market-radar) ficam ocultos por enquanto -- as
// rotas, o código e os dados continuam intactos para um eventual retorno,
// só o acesso visível na navegação principal foi removido.
const navSections = [
  {
    label: 'Principal',
    items: [
      { path: '/dashboard', icon: LayoutGrid, label: 'Visão geral' },
      { path: '/activities', icon: ListChecks, label: 'Atividades' },
      { path: '/listing-creator', icon: Sparkles, label: 'Criar anúncio' },
      { path: '/marketplace', icon: Store, label: 'Anúncios', badge: true },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { path: '/integrations', icon: Link2, label: 'Integrações' },
      { path: '/settings', icon: Settings, label: 'Configurações' },
    ],
  },
];

const navItems = navSections.flatMap((s) => s.items);

const findNavContext = (pathname) => {
  for (const section of navSections) {
    const item = section.items.find(
      (i) => pathname === i.path || pathname.startsWith(`${i.path}/`)
    );
    if (item) return { section, item };
  }
  return null;
};

const Breadcrumb = () => {
  const location = useLocation();
  const ctx = findNavContext(location.pathname);
  if (!ctx) return null;
  return (
    <div
      className="hidden lg:flex items-center gap-1.5 px-6 py-2.5 text-xs border-b bg-white"
      style={{ borderColor: 'var(--bk-border)' }}
      data-testid="breadcrumb-bar"
    >
      <span className="font-medium text-zinc-400">Central de Marketplace</span>
      <ChevronRight className="w-3 h-3 text-zinc-300" />
      <span className="font-medium text-zinc-400">{ctx.section.label}</span>
      <ChevronRight className="w-3 h-3 text-zinc-300" />
      <span className="font-semibold text-zinc-900">{ctx.item.label}</span>
    </div>
  );
};

export const Sidebar = ({ isOpen, setIsOpen, collapsed, setCollapsed }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-full z-50
          transform transition-all duration-200 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:static
          app-sidebar ${collapsed ? 'w-[76px]' : 'w-[256px]'}
        `}
        style={{
          background: 'var(--bk-black)',
          color: 'var(--bk-cream)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex flex-col h-full">
          {/* Brand header */}
          <div className={`h-[72px] flex items-center ${collapsed ? 'justify-center px-3' : 'px-5'} border-b border-white/[0.06]`}>
            <div className="flex items-center gap-2.5">
              {collapsed ? (
                <Logo variant="light" size={26} className="rounded-md" />
              ) : (
                <Logo variant="wordmark" showWordmarkOnly size={21} />
              )}
              {!collapsed && (
                <span className="text-[9px] uppercase tracking-[0.16em] text-white/35 font-semibold ml-2 border border-white/10 rounded-full px-2 py-0.5">
                  OS
                </span>
              )}
            </div>
          </div>

          {/* Collapse button */}
          <div className="hidden lg:flex justify-end px-2.5 py-2">
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1.5 rounded-md text-white/35 hover:text-white hover:bg-white/[0.07] transition"
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
              data-testid="sidebar-collapse-btn"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2.5 py-1 overflow-y-auto">
            {collapsed ? (
              <div className="space-y-0.5">
                {navItems.map(({ path, icon: Icon, label }) => (
                  <NavLink
                    key={path}
                    to={path}
                    onClick={() => setIsOpen(false)}
                    className={({ isActive }) => `
                      flex items-center justify-center p-2.5 rounded-lg
                      transition-all duration-150 relative group
                      ${isActive
                        ? 'bg-white text-zinc-950 shadow-sm'
                        : 'text-white/55 hover:bg-white/[0.07] hover:text-white'}
                    `}
                    data-testid={`nav-${path.slice(1).replace(/\//g, '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="absolute left-full ml-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 border border-white/10">
                      {label}
                    </span>
                  </NavLink>
                ))}
              </div>
            ) : (
              <div className="space-y-5 py-2">
                {navSections.map((section) => (
                  <div key={section.label}>
                    <p className="text-[9px] font-semibold text-white/30 uppercase tracking-[0.18em] px-3 mb-2">
                      {section.label}
                    </p>
                    <div className="space-y-0.5">
                      {section.items.map(({ path, icon: Icon, label, badge }) => (
                        <NavLink
                          key={path}
                          to={path}
                          onClick={() => setIsOpen(false)}
                          className={({ isActive }) => `
                            flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px]
                            transition-all duration-150 relative
                            ${isActive
                              ? 'bg-white text-zinc-950 font-semibold shadow-sm'
                              : 'text-white/60 hover:bg-white/[0.07] hover:text-white'}
                          `}
                          data-testid={`nav-${path.slice(1).replace(/\//g, '-')}`}
                        >
                          <Icon className="w-[16px] h-[16px] flex-shrink-0" strokeWidth={1.8} />
                          <span className="truncate">{label}</span>
                          {badge && (
                            <Badge className="ml-auto bg-zinc-200 text-zinc-700 border-0 text-[8px] px-1.5 py-0">
                              novo
                            </Badge>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </nav>

          {/* User section */}
          <div className="p-3 border-t border-white/[0.06] bg-white/[0.02]">
            {!collapsed ? (
              <>
                <div className="flex items-center gap-3 px-2 py-2">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0"
                    style={{
                      background: 'var(--bk-cream)',
                      color: 'var(--bk-black)',
                    }}
                  >
                    {user?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{user?.name || '—'}</p>
                    <p className="text-xs text-white/50 truncate">{user?.email}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 text-white/60 hover:text-white hover:bg-white/5 mt-1 h-9"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm">Sair</span>
                </Button>
              </>
            ) : (
              <button
                onClick={handleLogout}
                className="w-full flex justify-center p-2 rounded-md text-white/60 hover:text-white hover:bg-white/5"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export const Header = ({ setIsOpen }) => {
  const location = useLocation();
  const currentItem = navItems.find((item) =>
    location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
  );

  return (
    <header
      className="app-mobile-header sticky top-0 z-30 backdrop-blur-xl border-b px-4 lg:hidden"
      style={{
        borderColor: 'var(--bk-border)',
      }}
    >
      <div className="h-16 flex items-center justify-between gap-3">
        <button
          onClick={() => setIsOpen(true)}
          className="w-10 h-10 inline-flex items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-700 shadow-sm hover:bg-zinc-50"
          data-testid="mobile-menu-btn"
          aria-label="Abrir navegação"
        >
          <Menu className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <Logo variant="dark" showWordmarkOnly size={18} />
          <p className="text-[10px] leading-none text-zinc-400 truncate mt-1">
            {currentItem?.label || 'Marketplace Growth OS'}
          </p>
        </div>
        <div className="w-10 h-10 rounded-lg bg-zinc-950 text-white flex items-center justify-center text-[10px] font-bold tracking-wide">
          BK
        </div>
      </div>
    </header>
  );
};

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(
    () => typeof window !== 'undefined' && window.innerWidth < 1280
  );

  return (
    <div className="app-shell min-h-screen flex">
      <Sidebar
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
      />
      <div className="flex-1 flex flex-col min-w-0">
        <Header setIsOpen={setSidebarOpen} />
        <Breadcrumb />
        <main className="app-main flex-1">
          <div className="app-main-inner">{children}</div>
        </main>
      </div>
    </div>
  );
};
